import React from 'react'
import { Flex, Avatar, Text, FlexProps, AvatarProps, BoxProps, Tooltip } from '@chakra-ui/core'

import useContextInfo from 'hooks/useContextInfo'
import { css } from '@emotion/core'

interface ContextDisplayProps {
  contextId: string
  showAvatar?: boolean
  showContextName?: boolean
  tag?: string
  // Avatar customization
  avatarProps?: Omit<AvatarProps, 'src' | 'alt'>
  // Text customization
  fontSize?: string | number
  fontWeight?: string | number
  textColor?: string
  textProps?: BoxProps
  // Container customization
  containerProps?: FlexProps
  gap?: number
  description?: string
  showGroupName?: boolean
}

/**
 * Universal component to display context (user or group) avatar and name
 * @param contextId - User ID or Group ID
 * @param showAvatar - Show avatar (default: true)
 * @param showContextName - Show context name (default: true)
 * @param avatarSize - Avatar size in pixels (default: 40)
 * @param avatarProps - Additional props for avatar Image component
 * @param fontSize - Font size for name text (default: '14px')
 * @param fontWeight - Font weight for name text (default: '500')
 * @param textColor - Text color for name (default: '#1e293b')
 * @param textProps - Additional props for Text component
 * @param containerProps - Additional props for Flex container
 * @param gap - Gap between avatar and text in pixels (default: 10)
 */
const ContextDisplay: React.FC<ContextDisplayProps> = ({
  contextId,
  showAvatar = true,
  showContextName = true,
  tag,
  avatarProps = {},
  fontSize = '14px',
  fontWeight = '500',
  textColor = '#1e293b',
  textProps = {},
  containerProps = {},
  gap = 10,
  description,
  showGroupName = false,
}) => {
  const { name, avatar, groupName } = useContextInfo(contextId)
  const descriptionText = description || (showGroupName ? groupName : undefined)

  // If both are hidden, return null
  if (!showAvatar && !showContextName) {
    return null
  }

  return (
    <Tooltip label={name} placement="bottom" hasArrow aria-label={name} zIndex={10000}>
      <Flex alignItems="center" style={{ gap: `${gap}px` }} {...containerProps}>
        {showAvatar && <Avatar src={avatar} name={name} width="40px" height="40px" {...avatarProps} />}
        <Flex flexDir="column">
          {showContextName && (
            <Text
              flex="1"
              isTruncated
              fontSize={fontSize}
              fontWeight={fontWeight}
              color={textColor}
              lineHeight="1.4"
              css={css`
                max-width: 250px;
                @media (max-width: 1400px) {
                  max-width: 150px;
                }
                @media (max-width: 1200px) {
                  max-width: 100px;
                }
                @media (max-width: 768px) {
                  max-width: 80px;
                }
              `}
              {...textProps}
            >
              {name}
            </Text>
          )}
          {tag && (
            <Text
              fontSize="12px"
              fontWeight="500"
              color="#3B82F6"
              bg="#DBEAFE"
              borderRadius="4px"
              px="6px"
              py="2px"
              isTruncated
              width="fit-content"
              css={css`
                max-width: 250px;
                display: inline-block;
                @media (max-width: 1400px) {
                  max-width: 150px;
                }
                @media (max-width: 1200px) {
                  max-width: 100px;
                }
                @media (max-width: 768px) {
                  max-width: 80px;
                }
              `}
            >
              {tag}
            </Text>
          )}
          {descriptionText && (
            <Text fontSize="11px" color="#9CA3AF">
              {descriptionText}
            </Text>
          )}
        </Flex>
      </Flex>
    </Tooltip>
  )
}

export default ContextDisplay
